import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminEmpresaService } from '../../shared/services';
import { Building2, Save, X } from 'lucide-react';

interface EmpresaFormData {
  nome: string;
  cnpj: string;
  ativo: boolean;
  email?: string;
  telefone?: string;
  endereco?: string;
  responsavel?: string;
}

export function AdminEmpresaFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [formData, setFormData] = useState<EmpresaFormData>({
    nome: '',
    cnpj: '',
    ativo: true,
    email: '',
    telefone: '',
    endereco: '',
    responsavel: '',
  });

  const { data: empresa } = useQuery({
    queryKey: ['admin-empresa', id],
    queryFn: () => adminEmpresaService.getEmpresa(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (empresa) {
      setFormData({
        nome: empresa.nome || '',
        cnpj: empresa.cnpj || '',
        ativo: empresa.ativo ?? true,
        email: empresa.email || '',
        telefone: empresa.telefone || '',
        endereco: empresa.endereco || '',
        responsavel: empresa.responsavel || '',
      });
    }
  }, [empresa]);

  const createMutation = useMutation({
    mutationFn: (data: EmpresaFormData) => adminEmpresaService.createEmpresa(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-empresas'] });
      navigate('/admin/empresas');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: EmpresaFormData) => {
      console.log('📝 [AdminEmpresaFormPage] Iniciando atualização da empresa');
      console.log('📊 [AdminEmpresaFormPage] ID:', id);
      console.log('📦 [AdminEmpresaFormPage] FormData:', data);
      return adminEmpresaService.updateEmpresa(id!, data);
    },
    onSuccess: (response) => {
      console.log('✅ [AdminEmpresaFormPage] Empresa atualizada com sucesso:', response);
      queryClient.invalidateQueries({ queryKey: ['admin-empresas'] });
      queryClient.invalidateQueries({ queryKey: ['admin-empresa', id] });
      navigate('/admin/empresas');
    },
    onError: (error) => {
      console.error('❌ [AdminEmpresaFormPage] Erro ao atualizar empresa:', error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 [AdminEmpresaFormPage] Formulário submetido');
    console.log('📊 [AdminEmpresaFormPage] isEdit:', isEdit);
    console.log('📦 [AdminEmpresaFormPage] FormData atual:', formData);
    
    if (isEdit) {
      console.log('✏️ [AdminEmpresaFormPage] Modo edição - chamando updateMutation');
      updateMutation.mutate(formData);
    } else {
      console.log('➕ [AdminEmpresaFormPage] Modo criação - chamando createMutation');
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            {isEdit ? 'Editar Empresa' : 'Nova Empresa'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEdit ? 'Atualize os dados da empresa cliente' : 'Cadastre uma nova empresa cliente'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Empresa *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nome da empresa"
              />
            </div>

            {/* CNPJ */}
            <div>
              <label htmlFor="cnpj" className="block text-sm font-medium text-gray-700 mb-2">
                CNPJ
              </label>
              <input
                type="text"
                id="cnpj"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="00.000.000/0000-00"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email de Contato
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="contato@empresa.com.br"
              />
            </div>

            {/* Telefone */}
            <div>
              <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                Telefone
              </label>
              <input
                type="tel"
                id="telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(11) 99999-9999"
              />
            </div>

            {/* Responsável */}
            <div>
              <label htmlFor="responsavel" className="block text-sm font-medium text-gray-700 mb-2">
                Responsável
              </label>
              <input
                type="text"
                id="responsavel"
                name="responsavel"
                value={formData.responsavel}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nome do responsável"
              />
            </div>

            {/* Endereço */}
            <div>
              <label htmlFor="endereco" className="block text-sm font-medium text-gray-700 mb-2">
                Endereço
              </label>
              <textarea
                id="endereco"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Endereço completo"
              />
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                name="ativo"
                checked={formData.ativo}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="ativo" className="text-sm font-medium text-gray-700">
                Empresa ativa
              </label>
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isEdit ? 'Salvar Alterações' : 'Criar Empresa'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/empresas')}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-5 h-5" />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
